import { useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import { IconButton } from './Button'
import { useDialogBehaviour } from './useDialogBehaviour'

export interface SheetProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /**
   * `sheet`: slides up from the bottom on phones, centred on wide screens (default).
   * `modal`: always centred. `fullscreen`: covers the screen (scanner).
   */
  variant?: 'sheet' | 'modal' | 'fullscreen'
  /** Prevent closing by Escape / backdrop (e.g. while saving). */
  dismissible?: boolean
  className?: string
}

export function Sheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  variant = 'sheet',
  dismissible = true,
  className,
}: SheetProps) {
  const { t } = useI18n()
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descId = useId()
  useDialogBehaviour(open, onClose, panelRef, dismissible)

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 animate-fade-in bg-ink/40"
        aria-hidden="true"
        onClick={() => dismissible && onClose()}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'relative flex max-h-[92dvh] w-full flex-col bg-paper shadow-sheet focus:outline-none',
          variant === 'sheet' &&
            'animate-sheet-up rounded-t-card sm:max-w-lg sm:animate-fade-in sm:rounded-card',
          variant === 'modal' && 'mx-4 max-w-lg animate-fade-in rounded-card',
          variant === 'fullscreen' && 'h-[100dvh] max-h-none animate-fade-in',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-rule px-4 pb-3 pt-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[19px] leading-snug text-ink">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-[14px] text-muted">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <IconButton
              label={t('common.close')}
              icon={<X />}
              onClick={onClose}
              className="-mr-2 -mt-1"
            />
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && (
          <div className="pb-safe flex gap-2 border-t border-rule bg-paper px-4 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** Alias for desktop dialogs. */
export function Modal(props: Omit<SheetProps, 'variant'>) {
  return <Sheet {...props} variant="modal" />
}
